import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema, buildTechArticleSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'What issue tracking should a startup use?',
    a: 'A startup should use issue tracking that is free to start, priced without seat fees as it grows, and fast to set up. Kora starts with 1,100 free credits, unlimited members, AI ticket structuring free — then pay-as-you-go credits at $1 for 10 (a ticket costs 1 credit), which covers most startups from first customer to Series A. Jira and Linear are strong for pure engineering teams; the tradeoff is running a second system for customer-facing tickets.',
  },
  {
    q: 'Why not just use Jira or a spreadsheet?',
    a: 'Spreadsheets lose requests silently — no status, no owner, no history, and no API. Jira works well for engineering sprints but is heavyweight for customer tickets: every request needs manual structuring, and non-technical teammates often avoid it. Startups usually need one place for messy input from anywhere (Slack, email, WhatsApp) that becomes structured automatically — which is exactly the gap AI-native trackers fill.',
  },
  {
    q: 'How much does issue tracking cost for a startup?',
    a: 'It can be $0 for a long time. Kora starts with 1,100 free credits and unlimited members and stays usage-based after: $1 = 10 credits, 1 credit per ticket created. Per-seat alternatives scale with headcount: a 10-person team on Freshdesk Growth pays $150/month, on Zendesk Suite Team $550/month. Paying per ticket instead of per seat keeps tool spend tied to actual work exactly when hiring accelerates.',
  },
];

export function IssueTrackingForStartupsPage() {
  useEffect(() => {
    const title = 'Issue Tracking for Startups — How to Pick a Tool';
    const description =
      'Issue tracking for startups: what matters at seed stage, the real cost of per-seat tools, and how to pick a system you will not outgrow.';
    setDocumentMeta(title, description);
    setCanonical('/issue-tracking-for-startups');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/issue-tracking-for-startups', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Issue Tracking for Startups', url: 'https://usekora.app/issue-tracking-for-startups' },
      ]),
      buildTechArticleSchema(title, description, '2026-09-20'),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Guide · Updated September 2026"
      title="Issue tracking for startups"
      subtitle="What a startup actually needs from issue tracking — and the cost traps that surprise teams at 10+ people."
      answer={
        <p>
          For startups, the right issue tracking is cheap to start, free of seat fees when growing,
          and structured automatically. The real-world pattern: requests arrive messy — Slack
          threads, customer emails, WhatsApp forwards — and a startup cannot afford manual triage
          or per-seat pricing that scales with every hire. Kora has unlimited members and no seat fees, with AI
          ticket structuring free: you start with 1,100 free credits, then pay $0.10 per ticket ($1 = 10 credits). Per-seat alternatives compound instead: a
          10-person team pays $190/month on Freshdesk Growth and $550/month on Zendesk Suite
          Team. The other startup trap is running two systems — one for engineering (Jira,
          Linear), one for customers — when one AI-native tracker handles both.
        </p>
      }
    >
      <ContentSection title="What a startup actually needs">
        <p>
          Not the enterprise checklist. Four things carry a startup through its first two years:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Low cost while small.</strong> The tool should not charge for seats at 2–10
            people — no 6-month caps, no seat minimums.
          </li>
          <li>
            <strong>Predictable cost when hiring.</strong> No-seat pricing means adding person #11
            does not add a line item. Per-seat pricing means your tooling bill grows exactly when
            payroll grows.
          </li>
          <li>
            <strong>Automatic structure for messy input.</strong> Startup requests arrive as
            Slack threads and forwarded emails, not clean web forms. Manual triage is the first
            thing that breaks.
          </li>
          <li>
            <strong>An API from day one.</strong> Not for integrations you will build later, but
            for the ones your customers and automations will demand.
          </li>
        </ul>
      </ContentSection>

      <ContentSection title="The per-seat trap, in numbers">
        <p>
          Per-seat pricing feels cheap at 3 people and compounds at 15. On Freshdesk Growth
          ($19/agent/month), a startup pays $57/month at 3 agents, $190 at 10, and $475 at 25. On
          Zendesk Suite Team ($55/agent/month): $165, $550, and $1,375. Kora stays usage-based at $0.10 per ticket — 300 tickets a month is $30 whether
          you have 3 people or 25, because members are unlimited. Competitor pricing
          checked 2026-10-19.
        </p>
      </ContentSection>

      <ContentSection title="One system or two?">
        <p>
          The classic startup stack is Jira or Linear for engineering plus a support tool for
          customers. It works — until the same bug is tracked twice, or a customer request needs
          an engineering fix and lives in the wrong system. AI-native trackers that accept any
          input and structure it automatically let one system serve both audiences: customer
          tickets and internal issues in the same place, with categories doing the separation.
          Engineering-only teams with mature sprint rituals are the exception —{' '}
          <Link to="/compare/kora-vs-jira" className="underline hover:text-zinc-900 ">Jira</Link>{' '}
          and{' '}
          <Link to="/compare/kora-vs-linear" className="underline hover:text-zinc-900 ">Linear</Link>{' '}
          remain excellent for that case.
        </p>
      </ContentSection>

      <ContentSection title="A picking process that works">
        <p>
          Take your last 20 real requests — actual Slack threads, actual emails — and put them
          into the trial. Check three things: did structuring get category and priority right
          without edits, how long setup actually took, and what the bill would be at 2× your
          current headcount. That last number is the one startups regret not calculating. Kora's
          1,100 free credits (unlimited members, free AI ticket structuring) are
          enough to run this test on real volume.
        </p>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions startup founders ask about issue tracking." />
    </ContentPage>
  );
}
